import Link from "next/link";
import Image from "next/image";
import { EnvelopeIcon, MapPinIcon, PhoneIcon } from "@heroicons/react/24/outline";

import getLanguage from "@/app/utils/getlanguage";

import translation from "./locale/index.json";
import logoWhite from "../../../public/images/logo-white.svg";

const normalizePath = (path: string) => {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
};

const localizeHref = (path: string, locale: string) => {
  const normalized = normalizePath(path);
  if (normalized === "/" || normalized === `/${locale}`) return `/${locale}`;
  return `/${locale}${normalized}`;
};

interface FooterProps {
  locale: string;
}

const sanitize = (value: unknown) => (typeof value === "string" ? value.trim() : "");

export default function Footer({ locale }: FooterProps) {
  const typedContent = (getLanguage(translation, locale) ?? {}) as Record<string, string | undefined>;
  const getField = (key: string) => sanitize(typedContent[key]);

  const address = getField("address") || "ul. Nowolipie 26/70, 01-011 Warszawa";
  const email = getField("email") || "yourdentalclub@gmail.com";
  const phone = getField("phone") || "+48 720 840 150";

  const navLinks = [
    { href: localizeHref("/ortodonta", locale), label: getField("ortodonta") || "Ortodoncja" },
    { href: localizeHref("/first-visit-to-the-dentist", locale), label: getField("dentysta") || "Dentysta" },
    { href: localizeHref("/cennik", locale), label: getField("cennik") || "Cennik" },
    { href: localizeHref("/privacy-policy", locale), label: getField("privacy") || "Polityka prywatności" },
    { href: localizeHref("/terms-of-service", locale), label: getField("terms") || "Regulamin" },
  ];

  return (
    <div className="brand-gradient mt-20 rounded-t-3xl px-6 py-12 text-sm text-white">
      <div className="container mx-auto">
        <div className="grid gap-6 sm:grid-cols-6">
          <div className="flex flex-col gap-4 sm:col-span-2">
            <Link href={localizeHref("/", locale)}>
              <Image src={logoWhite} alt="Aleksandrowicz Dental Service Logo" className="h-12 w-auto" />
            </Link>
            <p className="max-w-sm text-sm text-white/80">{getField("info")}</p>
          </div>

          <div className="flex flex-col gap-2 text-white/80 sm:col-span-2">
            <span className="text-xs uppercase tracking-[0.3em] text-white/60">Usługi</span>
            {navLinks.map(link => (
              <Link key={link.href} className="transition hover:text-white" href={link.href}>
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex flex-col gap-4 text-white/80 sm:col-span-2">
            <span className="text-xs uppercase tracking-[0.3em] text-white/60">Kontakt</span>
            <div className="flex flex-col gap-3">
              <Link
                target="_blank"
                rel="noreferrer"
                href="https://maps.app.goo.gl/7aJKLNePwAHchhbb9"
                className="flex items-center gap-2 transition hover:text-white"
              >
                <MapPinIcon className="h-5 w-5" />
                {address}
              </Link>
              {email ? (
                <Link href={`mailto:${email}`} className="flex items-center gap-2 transition hover:text-white">
                  <EnvelopeIcon className="h-5 w-5" />
                  {email}
                </Link>
              ) : null}
              {phone ? (
                <Link
                  href={`tel:${phone.replace(/\s+/g, "")}`}
                  className="flex items-center gap-2 transition hover:text-white"
                >
                  <PhoneIcon className="h-5 w-5" />
                  {phone}
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
